import { useLiveQuery } from 'dexie-react-hooks';
import { estadoResultados, type FormaPagoVenta } from '@abastece/core';
import { addMoney, centavos, formatMoney, type Centavos } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { cobradoPorForma } from '../lib/cuadrar.js';
import { asientosDelRango, ventasDelPeriodo } from '../lib/libros.js';
import { useT, type Clave } from '../i18n/index.js';
import { Dato, enDolares } from './CuentasDeCaja.js';

/**
 * What the days left, for the owner: what was sold and what it cost, the expenses, and what was
 * left over, from the books; how the sales were paid; and what went to the bank. The days are
 * the ones being gone over (`diasPorCuadrar`), each counted by its own date, whichever day the
 * money was received or the expense written down.
 */

const FORMAS: { forma: FormaPagoVenta; clave: Clave }[] = [
  { forma: 'efectivo_cordobas', clave: 'pago.efectivo_cordobas' },
  { forma: 'efectivo_dolares', clave: 'pago.efectivo_dolares' },
  { forma: 'transferencia', clave: 'pago.transferencia' },
  { forma: 'billetera', clave: 'pago.billetera' },
  { forma: 'tarjeta', clave: 'pago.tarjeta' },
  { forma: 'credito', clave: 'pago.fiado' },
];

export function LoQueDejoElDia({ desde, hasta }: { desde: string; hasta: string }) {
  const t = useT();
  const datos = useLiveQuery(
    async () => {
      const rango = { desde, hasta };
      const [asientos, ventas, filas] = await Promise.all([
        asientosDelRango(rango),
        ventasDelPeriodo(rango),
        db.depositos.where('fecha').between(desde, hasta, true, true).toArray(),
      ]);
      // What went to the bank and stayed there: not one voided, nor cash brought back from it.
      const depositos = filas.filter((d) => !d.delBanco && !d.anulado);
      return {
        resultados: estadoResultados(asientos, rango),
        cobrado: cobradoPorForma(ventas),
        alBanco: {
          cordobas: depositos.reduce<Centavos>((a, d) => addMoney(a, d.cordobas), centavos(0)),
          dolares: depositos.reduce<Centavos>((a, d) => addMoney(a, d.dolares), centavos(0)),
        },
      };
    },
    [desde, hasta],
  );
  if (!datos) return null;
  const { resultados: r, cobrado, alBanco } = datos;
  const margen = r.margenBrutoBp === null ? null : `${(r.margenBrutoBp / 100).toFixed(1)}%`;

  return (
    <div className="flex flex-col gap-4" data-lo-que-dejo>
      <section className="tarjeta flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-slate-300">{t('dejo.titulo')}</h3>
        <Dato etiqueta={t('dejo.ingresos')} valor={formatMoney(r.totalIngresos)} />
        <Dato etiqueta={t('dejo.costo')} valor={formatMoney(centavos(-r.totalCostos), { signed: true })} />
        <Dato
          etiqueta={margen ? t('dejo.ganancia', { margen }) : t('dejo.gananciaSinMargen')}
          valor={formatMoney(r.utilidadBruta)}
        />
        <Dato etiqueta={t('dejo.gastos')} valor={formatMoney(centavos(-r.totalGastos), { signed: true })} />
        <div
          className={`mt-1 flex items-baseline justify-between gap-4 rounded-xl px-4 py-3 ${
            r.utilidadNeta >= 0 ? 'bg-emerald-500/10 text-emerald-200' : 'bg-rose-500/10 text-rose-200'
          }`}
        >
          <span className="min-w-0 text-sm font-medium">{t(r.utilidadNeta >= 0 ? 'dejo.quedo' : 'dejo.perdio')}</span>
          <span className="tabular shrink-0 text-xl font-bold">{formatMoney(centavos(Math.abs(r.utilidadNeta)))}</span>
        </div>
      </section>

      <section className="tarjeta flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-slate-300">{t('dejo.cobrado', { n: cobrado.ventas })}</h3>
        {cobrado.ventas === 0 && <p className="text-sm text-slate-400">{t('dejo.sinVentas')}</p>}
        {FORMAS.filter(({ forma }) => (cobrado.porForma[forma] ?? 0) !== 0).map(({ forma, clave }) => (
          <Dato
            key={forma}
            etiqueta={t(clave)}
            valor={
              forma === 'efectivo_dolares'
                ? `${enDolares(cobrado.dolares)} · ${formatMoney(cobrado.porForma[forma]!)}`
                : formatMoney(cobrado.porForma[forma]!)
            }
          />
        ))}
      </section>

      {(alBanco.cordobas > 0 || alBanco.dolares > 0) && (
        <section className="tarjeta flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-slate-300">{t('dejo.alBanco')}</h3>
          {alBanco.cordobas > 0 && <Dato etiqueta={t('deposito.alBancoCordobas')} valor={formatMoney(alBanco.cordobas)} />}
          {alBanco.dolares > 0 && <Dato etiqueta={t('deposito.alBancoDolares')} valor={enDolares(alBanco.dolares)} />}
        </section>
      )}
    </div>
  );
}
